import type {
  BoardRole,
  BoardTheme,
  CareerStage,
  CaseStudy,
  Credential,
  Education,
  Endorsement,
  Evidence,
  Lens,
  NavItem,
} from '../types/portfolio'

export const portrait =
  'https://cdn.magicpatterns.com/uploads/htAVHK93dUg88pnmbrqcWH/WhatsApp_Image_2026-09-14_at_6.42.54_PM.jpg'

export const identity = {
  name: 'Nigel Grierson',
  role: 'Senior Independent Non-Executive Director',
  disciplines:
    'Technology · Investment · Growth Capital · Corporate Finance · M&A · Governance',
  statement:
    'Technology operating experience, institutional investment discipline and independent Board judgement — connecting technology, capital, strategy and governance at Board level.',
  location: 'London, United Kingdom',
}

export const navItems: NavItem[] = [
  { label: 'Proposition', href: '#proposition' },
  { label: 'Career', href: '#career' },
  { label: 'Board Appointment', href: '#appointment' },
  { label: 'Board Experience', href: '#boards' },
  { label: 'Contribution', href: '#contribution' },
  { label: 'Contact', href: '#contact' },
]

export const lenses: Lens[] = [
  {
    label: 'Operator',
    statement: '20+ years in technology operating leadership.',
    detail:
      'Product development, marketing and senior general management — a decade at Intel Corporation and a decade across other technology operating roles.',
  },
  {
    label: 'Investor',
    statement: 'Institutional venture capital and technology investment experience.',
    detail:
      'Raised approximately $340m from more than 23 institutional LP investors; deployed ~$350m at Intel Capital; 68 technology companies backed across Europe.',
  },
  {
    label: 'Board Director',
    statement: 'Independent Board, governance, capital and strategic oversight.',
    detail:
      'Thirteen board seats as Director, Supervisory Director, Observer and Advisor — today on the board of an LSE listed company.',
  },
]

export const propositionNote =
  'Significant experience in company financing, raising and managing venture capital funds, advising on business strategy and building high-performance management teams.'

export const fiduciaryStatement =
  'In all positions I have personally held and maintained regulatory, statutory and fiduciary responsibilities in the relevant jurisdictions I have operated in (UK, Europe and the U.S).'

export const evidence: Evidence[] = [
  { value: '20+ years', label: 'Technology operating experience' },
  { value: 'Approx. $700m', label: 'Capital raised, invested & managed' },
  { value: '68', label: 'Technology companies invested in' },
  { value: '13', label: 'Board seats as Director, Observer or Advisor' },
  { value: 'Approx. $343m', label: 'Raised from institutional LP investors' },
]

export const careerStages: CareerStage[] = [
  {
    era: '1985 — 1996',
    organisation: 'Intel Corporation',
    role: 'Senior Management, Marketing & Product Development',
    lens: 'Technology operating leadership',
    summary:
      'A decade at Intel Corporation as an operating executive — part of two decades in the IT technology industry — building the technical and commercial judgement that later underpinned an investment career.',
    points: [
      'Progressive senior management roles across general management',
      'Product development and marketing leadership on global technology platforms',
      'Built international partnerships and market development programmes',
    ],
  },
  {
    era: '1996 — 2000',
    organisation: 'Intel Capital',
    role: 'Co-Managing Director',
    lens: 'Corporate venture investing',
    summary: 'European technology corporate venture fund.',
    points: [
      'Led and managed investments of ~$350m into 31 companies',
      'Hired and developed a high-performance team of 7 investment professionals',
      'Developed and managed the fund investment allocation strategy and risk oversight',
      'Supported exit transactions for Bookham, Lastminute.com, Gomez and others',
      'Delivered a top-quartile performance and #1 European corporate investor',
    ],
  },
  {
    era: '2000 — 2017',
    organisation: 'Doughty Hanson Technology Ventures',
    role: 'Co-Founding Managing Partner',
    lens: 'Institutional venture capital & fund management',
    summary: 'European technology venture capital fund.',
    points: [
      'Formed and managed a team of 7 investment professionals based in London & Munich',
      'Raised approximately $340m from more than 23 institutional LP investors based on personal track record',
      "Full accountability to LP's for investments, management and fund performance",
      'Developed a portfolio of 37 companies, over 100 transactions',
      'Delivered profitable returns to investors for the 2000 and 2010 vintage funds',
      'Appointed or held non-executive board positions in every portfolio company',
      'Held full accountability and responsibility for FCA and fund management obligations',
    ],
  },
  {
    era: '2023 — Present',
    organisation: 'Frontier IP Group plc',
    meta: 'LSE:FIPP',
    role: 'Non-Executive Board Director',
    lens: 'Public-company Board leadership',
    summary:
      'LSE listed company holding a portfolio of high technology companies, with full fiduciary, statutory and regulatory oversight as an independent director.',
    points: [
      'Member of the audit, remuneration and nomination committees',
      'Full support over several rounds of raising growth capital from the public markets',
      'Board-level critique of portfolio capital allocation, financial planning and reporting',
    ],
  },
  {
    era: '2020 — Present',
    organisation: 'Westlecot Business Consultants',
    role: 'Director & Owner',
    lens: 'Advisory & Board strategy',
    summary:
      'Advisory practice supporting technology boards, founders and institutional investors.',
    points: [
      'Board and growth strategy counsel for scaling technology businesses',
      'Fundraising, capital structure and exit readiness advisory',
      'Commercial and technical due diligence for institutional investors',
    ],
  },
]

export const frontierThemes: BoardTheme[] = [
  {
    theme: 'Governance',
    items: [
      'Full fiduciary, statutory and regulatory oversight of a UK LSE listed company',
      'Member of the audit, remuneration and nomination committees',
      'Critique and guidance of financial planning and reporting',
    ],
  },
  {
    theme: 'Capital & Finance',
    items: [
      'Full support over several rounds of raising growth capital from the public markets',
      'Critique and guidance at board level on portfolio capital allocation strategies',
      'Providing senior level introductions to financing 3rd parties',
    ],
  },
  {
    theme: 'Strategy',
    items: [
      'Providing valuable insight on growth strategy and potential',
      'Available to portfolio companies for strategy and financing advice to drive growth',
      'Providing senior level introductions to industry executives',
    ],
  },
  {
    theme: 'Technology & Risk',
    items: [
      'Providing the board with technology critique of portfolio companies to manage risk',
    ],
  },
]

export const chairSupport = 'Valuable sounding board for the Chair, Professor Dame Julia King.'

export const boardRoleTypes = [
  'Director',
  'Supervisory Director',
  'Board Observer',
  'Board Advisor',
]

export const sectors: string[] = [
  'Cyber Security',
  'Photonics & Semiconductors',
  'IP Telecommunications',
  'Online Marketplaces',
  'Data Storage & Analytics',
  'Micro Control Technology',
  'IP Commercialisation',
]

export const boardPortfolio: BoardRole[] = [
  {
    company: 'Garlik',
    role: 'Non-Executive Board Director',
    period: '2005 — 2011',
    descriptor: 'Personal identity protection software platform',
    outcome: 'Acquired by Experian',
    achievements: [
      'Negotiated and led investments totalling $8m as part of company overall financing',
      'Contributed to strategy development with technology advisory board members Tim Berners-Lee, Professor Wendy Hall and Professor Nigel Shadbolt',
      'Significant contribution to the development of a strategy to drive growth',
      'Provided M&A transaction support for an acquisition by Experian',
      'Member of the remuneration and audit committees',
      'Facilitated seamless executives transition plan',
    ],
  },
  {
    company: 'AdaptiveMobile Security',
    role: 'Non-Executive Board Director',
    period: '2007 — 2017',
    descriptor: 'Mobile network cyber security software',
    outcome: 'Acquired by ENEA',
    achievements: [
      'Negotiated and led investments totalling $8m as part of company overall financing',
      'Restructured a complex employee stock option plan to focus on growth',
      'Structured short and long-term executive remuneration plans',
      'Managed negotiations for a raise of $5m secured venture debt capital',
      'Led a board sub-committee and 3rd party advisor for M&A activity',
      'Led a board sub-committee to rapidly recover from a significant cyber attack',
      'Mentoring and 1:1 support to the CEO and CFO to drive a growth strategy',
      'Member of the remuneration and audit committees',
    ],
  },
  {
    company: 'MegaZebra',
    role: 'Supervisory Board Director',
    period: '2014 — 2017',
    descriptor: 'Social and mobile games developer, Munich',
    achievements: [
      'Provided supervisory board governance, policy and oversight to the executive board',
      'Managed a substantial shareholder relationship',
      'Provided oversight of financial reporting for audit',
      'Supported the executive team in a product development expansion strategy',
    ],
  },
  {
    company: 'SecretSales.com',
    role: 'Non-Executive Board Director',
    period: '2013 — 2017',
    descriptor: 'Online retail marketplace',
    outcome: 'Acquired by Excalibur Group',
    achievements: [
      'Negotiated and led investments totalling $4m',
      'Led and supported negotiations to secure $5m venture debt into the business',
      'Member of the audit committee and led the remuneration committee',
      'Led a board sub-committee to manage corporate risk',
      'Developed an executive compensation and employee stock option scheme',
    ],
  },
  {
    company: 'Oniqua MRO Analytics',
    role: 'Board Advisor',
    period: '2012 — 2018',
    descriptor: 'MRO data analysis and management',
    outcome: 'Acquired by IBM',
    achievements: [
      'Provided full due diligence and financial assessment support for the acquisition of Oniqua',
      'Led the executive team to develop a significant market growth strategy',
      'Developed and implemented a CEO succession plan',
      'Hired a top-tier CEO in the U.S and ensured a seamless transition of leadership',
    ],
  },
  {
    company: 'Bookham Technology',
    role: 'Non-Executive Board Observer',
    period: '1998 — 2000',
    descriptor: 'Photonics transceivers',
    outcome: 'IPO 2000 on LSE and NASDAQ',
    achievements: [
      'Structured and led a $10m equity financing into the company',
      'Provided support for the company IPO on LSE and NASDAQ',
      'Provided technology assessment to the Intel strategic technology committee',
      'Delivered substantial shareholder returns on the investment',
    ],
  },
  {
    company: 'DeepStream Technologies',
    role: 'Non-Executive Board Director',
    period: '2004 — 2010',
    descriptor: 'Micro control technology and manufacturing',
    achievements: [
      'Negotiated and led investments totalling $15m as part of $30m overall',
      'Attracted and led negotiations with a tier-1 follow-on investor in 2007',
      'Led negotiations to secure ~$10m venture debt financing in 2007',
      "Made key partnership introductions to CEO's of Groupe Schneider and Klöckner-Moeller",
      'Member of the audit and remuneration committees',
      'Significant contribution to the development of product strategy and partner licensing',
      'Led a board sub-committee to determine the long-term growth strategy',
    ],
  },
  {
    company: 'Touchbase',
    role: 'Non-Executive Board Director',
    period: '2005 — 2008',
    descriptor: 'IP telecommunications systems',
    achievements: [
      'Negotiated and led investments totalling $5.6m',
      'Member of the remuneration committee',
      'Structured a sales performance remuneration policy',
      'Attracted a third board member as a fully independent non-executive director',
    ],
  },
  {
    company: 'Neartek',
    role: 'Non-Executive Board Director',
    period: '2001 — 2006',
    descriptor: 'Data storage',
    achievements: [
      'Negotiated and led investments totalling $18m',
      'Attracted and negotiated a further $10m equity investment from a tier-1 fund',
      'Led the transition of the company from a French S.A.S to a Delaware Inc',
      'Transitioned the TopCo from Paris to Boston, Massachusetts',
      'Led the search and appointment of a high-quality CEO, ex GM Compaq',
      'Provided business and growth strategy development support',
    ],
  },
  {
    company: 'Westlecot Holme Management Company',
    role: 'Non-Executive Board Director',
    period: '2016 — Present',
    descriptor: 'Asset manager of a 24-luxury apartment development',
    achievements: [
      'Responsible for all legal and fiduciary obligations to 24 leaseholder shareholders',
      'Board leadership on all critical aspects of safety, insurance and maintenance',
      'Responsible for critique of all commercial contracts and major expenditure decisions',
      'Led the search for, and appointed, a 3rd party property management company',
      'Lead person for all corporate governance, finance and risk management discussions',
    ],
  },
]

export const contribution: BoardTheme[] = [
  {
    theme: 'Strategy',
    items: ['Technology strategy', 'Growth strategy', 'Strategic partnerships'],
  },
  {
    theme: 'Capital',
    items: ['Capital allocation', 'Fundraising', 'Venture debt', 'Investor perspective'],
  },
  {
    theme: 'Transactions',
    items: ['M&A', 'Due diligence', 'Corporate finance'],
  },
  {
    theme: 'Governance',
    items: ['Audit', 'Remuneration', 'Risk', 'Fiduciary oversight'],
  },
  {
    theme: 'Leadership',
    items: ['CEO succession', 'Executive challenge', 'Management support'],
  },
]

export const caseStudies: CaseStudy[] = [
  {
    company: 'Bookham Technology',
    context: 'Photonics transceivers · Board Observer · 1998 — 2000',
    headline: 'IPO on LSE and NASDAQ',
    points: [
      'Structured and led a $10m equity financing into the company',
      'Provided support for the company IPO on LSE and NASDAQ',
      'Delivered substantial shareholder returns on the investment',
    ],
  },
  {
    company: 'Garlik',
    context: 'Identity protection software · NED · 2005 — 2011',
    headline: 'Acquired by Experian',
    points: [
      'Negotiated and led investments totalling $8m as part of company overall financing',
      'Provided M&A transaction support for an acquisition by Experian',
      'Facilitated seamless executives transition plan',
    ],
  },
  {
    company: 'AdaptiveMobile Security',
    context: 'Mobile network cyber security · NED · 2007 — 2017',
    headline: 'Acquired by ENEA',
    points: [
      'Negotiated and led investments totalling $8m as part of company overall financing',
      'Managed negotiations for a raise of $5m secured venture debt capital',
      'Led a board sub-committee to rapidly recover from a significant cyber attack',
    ],
  },
  {
    company: 'Oniqua MRO Analytics',
    context: 'MRO data analysis · Board Advisor · 2012 — 2018',
    headline: 'Acquired by IBM',
    points: [
      'Provided full due diligence and financial assessment support for the acquisition',
      'Developed and implemented a CEO succession plan',
      'Hired a top-tier CEO in the U.S and ensured a seamless leadership transition',
    ],
  },
]

export const endorsements: Endorsement[] = [
  {
    quote:
      'Nigel Grierson and Dr David Holbrook have been appointed Non-Executive Directors of Frontier IP as the Group broadens and deepens the experience on its Board.',
    source: 'Frontier IP Group plc',
    context: 'Board appointment announcement, March 2023',
  },
  {
    quote:
      'Nigel has 20 years’ operating experience in the IT industry with positions in product development, marketing and senior management. He has been a Managing Director of venture capital funds managing investments of over $500 million in 70 technology start-ups in Europe.',
    source: 'Frontier IP Group plc',
    context: 'Official board biography',
  },
  {
    quote:
      'Between 1984 and 2000 he held senior roles at Intel Corporation, becoming Co-Director EMEA for Intel Capital Group, developing and leading a team to establish Intel as a leading corporate technology investor.',
    source: 'Frontier IP Group plc',
    context: 'Board appointment statement',
  },
]

export const credentials: Credential[] = [
  { title: 'FCA Qualified', detail: 'Approved to run controlled functions' },
  { title: 'Associate, CISI', detail: 'Chartered Institute for Securities & Investment' },
  { title: 'Investment Management Certificate', detail: 'Securities & Investment Institute' },
  { title: 'Board Masterclass Lecturer', detail: 'Invest Europe' },
  { title: 'External Examiner', detail: 'Cranfield School of Management MBA' },
  { title: 'Registered Director', detail: 'Companies House · appointed 15 March 2023' },
]

export const education: Education[] = [
  {
    institution: 'INSEAD',
    programme: 'Advanced Management Programme',
    detail: 'Fontainebleau · Alumnus',
  },
  {
    institution: 'University of Bristol',
    programme: 'Undergraduate Degree',
    detail: 'United Kingdom',
  },
  {
    institution: 'Cranfield School of Management',
    programme: 'External Examiner, MBA',
    detail: 'Bettany Centre for Entrepreneurship',
  },
]

export const availability = {
  statement:
    'Open to Non-Executive Director and Board Advisory roles with technology and growth businesses.',
  disciplines: ['Technology', 'Investment', 'Capital', 'Strategy', 'Governance'],
}

export const contact = {
  email: 'nigelgrierson@outlook.com',
  linkedin: 'linkedin.com/in/nigelgrierson',
  linkedinUrl: 'https://www.linkedin.com/in/nigelgrierson/',
  website: 'nigelgrierson1.com',
  websiteUrl: 'https://nigelgrierson1.com',
}
